import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { userValidationService } from '@/features/user/services/userValidationService';
import { useAuth } from '@/hooks/useAuth';
// import type { UserValidationResponse } from '@/features/user/types/types';

export const useUserValidation = () => {
  const { setUser, setError, isAuthenticated, isLoading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setLocalError] = useState<string | null>(null);

  const validateUser = useCallback(async () => {
    // Proactive check: Skip API call if not authenticated
    if (!authLoading && !isAuthenticated) {
      setLocalError('Please log in to view your profile');
      navigate('/login');
      return null;
    }

    // Wait for auth check to complete
    if (authLoading) return null;

    setIsLoading(true);
    setError(null);
    setLocalError(null);

    try {
      const response = await userValidationService.validateUser();

      // Convert the validation response to our user format
      const userData = {
        id: response.data.user.ID,
        first_name: response.data.user.FirstName,
        last_name: response.data.user.LastName,
        dob: response.data.user.DOB,
        email: response.data.user.Email,
        phone_number: response.data.user.PhoneNumber,
        is_guest: response.data.user.IsGuest,
        checkout_cooldown_until: response.data.user.CheckoutCooldownUntil,
        gender: response.data.user.Gender,
        profile_picture_id: response.data.user.ProfilePictureID
      };

      // Update the auth store with the validated user data
      setUser(userData);

      return response;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to validate user';
      setError(errorMessage);
      setLocalError(errorMessage);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, [setUser, setError, isAuthenticated, authLoading, navigate]);

  return {
    validateUser,
    isLoading,
    error
  };
};
